import { useState, useEffect, useMemo } from "react";
import { 
  Calendar, 
  CalendarDays, 
  Mail, 
  CheckCircle,
  XCircle,
  Clock,
  Trash2,
  Eye,
  Plus,
  Edit,
  Users,
  User,
  LayoutDashboard,
  Upload,
  Image as ImageIcon,
  Images,
  Ticket,
  Building,
  Settings,
  Key
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AdminSettings({ credentials, userRole }) {
  const [passwords, setPasswords] = useState({
    internal: "",
    viewer: ""
  });
  const [saving, setSaving] = useState({ internal: false, viewer: false });

  const headers = useMemo(() => {
    const auth = btoa(`${credentials.username}:${credentials.password}`);
    return { Authorization: `Basic ${auth}` };
  }, [credentials.username, credentials.password]);

  const updatePassword = async (username) => {
    const password = passwords[username];
    
    if (!password) {
      toast.error("Please enter a new password");
      return;
    }
    
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setSaving(prev => ({ ...prev, [username]: true }));
    
    try {
      await axios.patch(`${API}/users/password`, {
        username,
        new_password: password
      }, { headers });
      
      toast.success(`Password updated for ${username}`);
      setPasswords(prev => ({ ...prev, [username]: "" }));
    } catch (error) {
      console.error("Error updating password:", error);
      const message = error.response?.data?.detail || "Failed to update password";
      toast.error(message);
    } finally {
      setSaving(prev => ({ ...prev, [username]: false }));
    }
  };

  if (userRole !== 'admin') {
    return (
      <div className="text-center py-12">
        <p className="text-[#78716C]">Admin access required</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-settings">
      <h1 className="heading-lg mb-8">Settings</h1>

      <Card className="card-heritage mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="w-5 h-5 text-[#14532D]" />
            User Password Management
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-[#78716C] mb-6">
            Update passwords for the internal and viewer accounts. The admin password cannot be changed here for security reasons.
          </p>

          <div className="space-y-6">
            {/* Internal User */}
            <div className="p-4 border border-[#E7E5E4] rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 bg-[#D97706] rounded-full flex items-center justify-center">
                  <Users className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="font-semibold">internal</p>
                  <p className="text-xs text-[#78716C]">Internal Booking Access Only</p>
                </div>
              </div>
              <div className="flex gap-3">
                <Input
                  type="password"
                  placeholder="Enter new password"
                  value={passwords.internal}
                  onChange={(e) => setPasswords(prev => ({ ...prev, internal: e.target.value }))}
                  className="input-heritage flex-1"
                  data-testid="internal-password-input"
                />
                <Button 
                  onClick={() => updatePassword('internal')}
                  disabled={saving.internal}
                  className="btn-primary"
                  data-testid="update-internal-password"
                >
                  {saving.internal ? "Saving..." : "Update"}
                </Button>
              </div>
            </div>

            {/* Viewer User */}
            <div className="p-4 border border-[#E7E5E4] rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 bg-[#6B7280] rounded-full flex items-center justify-center">
                  <Eye className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="font-semibold">viewer</p>
                  <p className="text-xs text-[#78716C]">View Only Access</p>
                </div>
              </div>
              <div className="flex gap-3">
                <Input
                  type="password"
                  placeholder="Enter new password"
                  value={passwords.viewer}
                  onChange={(e) => setPasswords(prev => ({ ...prev, viewer: e.target.value }))}
                  className="input-heritage flex-1"
                  data-testid="viewer-password-input"
                />
                <Button 
                  onClick={() => updatePassword('viewer')}
                  disabled={saving.viewer}
                  className="btn-primary"
                  data-testid="update-viewer-password"
                >
                  {saving.viewer ? "Saving..." : "Update"}
                </Button>
              </div>
            </div>
          </div>

          <div className="mt-6 p-4 bg-[#FEF3C7] rounded-lg">
            <p className="text-sm text-[#78350F]">
              <strong>Note:</strong> Password changes take effect immediately. Make sure to communicate new passwords to the relevant users.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Current User Info */}
      <Card className="card-heritage">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-[#14532D]" />
            User Accounts
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-[#F5F5F0] rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-[#14532D] rounded-full flex items-center justify-center">
                  <Users className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="font-semibold">admin</p>
                  <p className="text-xs text-[#78716C]">Full Access</p>
                </div>
              </div>
              <span className="text-xs bg-[#14532D] text-white px-2 py-1 rounded">Admin</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-[#F5F5F0] rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-[#D97706] rounded-full flex items-center justify-center">
                  <Building className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="font-semibold">internal</p>
                  <p className="text-xs text-[#78716C]">Internal Booking Only</p>
                </div>
              </div>
              <span className="text-xs bg-[#D97706] text-white px-2 py-1 rounded">Internal</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-[#F5F5F0] rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-[#6B7280] rounded-full flex items-center justify-center">
                  <Eye className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="font-semibold">viewer</p>
                  <p className="text-xs text-[#78716C]">View Only Access</p>
                </div>
              </div>
              <span className="text-xs bg-[#6B7280] text-white px-2 py-1 rounded">Viewer</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Staff Rota Component
